import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, DefaultButton, TextField } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import SearchableDropdown from "../atoms/searchable-dropdown";
import { type VehicleDevice, type VehicleDeviceUpdate } from "../../api";
import { createUuid, type UUID } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { doubleBindString } from "../../utils/double-bind";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { defaultPageSize } from "../../utils/constants";

export interface FormUpdateVehicleDeviceProps extends FormUpdateProps<VehicleDevice> {}

import { store } from "../../store";

@observer
export class FormUpdateVehicleDevice extends Component<FormUpdateVehicleDeviceProps> {
    private validationId = createUuid();

    constructor(props: FormUpdateVehicleDeviceProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryVehicleDevices.validation.update.updateModel(
            this.validationId,
            this.vehicleDeviceUpdate,
        );
    }

    componentDidMount(): void {
        store.repositoryVehicleDevices.validation.update.initializeModel(
            this.validationId,
            this.vehicleDeviceUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositoryVehicleDevices.discardMutableCopy(
            FormUpdateVehicleDevice.name,
            this.props.id,
        );
    }

    @computed private get vehicleDevice(): VehicleDevice | undefined {
        return store.repositoryVehicleDevices.mutableCopyById(
            FormUpdateVehicleDevice.name,
            this.props.id,
        );
    }

    @computed private get vehicleDeviceUpdate(): VehicleDeviceUpdate {
        const update: VehicleDeviceUpdate = pick(["vehicleId", "serialNumber"], this.vehicleDevice);

        return update;
    }

    @action.bound
    private async updateVehicleDevice(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const vehicleDevice = await store.repositoryVehicleDevices.update(
            this.props.id,
            this.vehicleDeviceUpdate,
        );
        this.props.onUpdate(vehicleDevice);
    }

    @action.bound private setVehicleDeviceType(vehicleId?: UUID): void {
        if (!this.vehicleDevice) {
            throw new Error("Can't update vehicleDevice type id: VehicleDevice not loaded.");
        }
        this.vehicleDevice.vehicleId = vehicleId ?? null;
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateVehicleDevice.submit.text")}
                validation={store.repositoryVehicleDevices.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateVehicleDevice}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <SearchableDropdown
                        formatEntity={store.i18n.formatVehicle}
                        repository={store.repositoryVehicles}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formUpdateVehicleDevice.vehicle.label")}
                        onChange={this.setVehicleDeviceType}
                        disabled={!this.vehicleDevice}
                        selectedKey={this.vehicleDevice?.vehicleId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicleDevices.validation.update.getFieldValidationState(
                                this.validationId,
                                "vehicleId",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicleDevice.serialNumber.label")}
                        {...doubleBindString(this.vehicleDevice!, "serialNumber", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicleDevices.validation.update.getFieldValidationState(
                                this.validationId,
                                "serialNumber",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicleDevice.currentFirmwareVersion.label")}
                        type="text"
                        disabled
                        value={this.vehicleDevice?.currentVersion}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateVehicleDevice.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
